import { useEffect, useState } from 'react'
import { translate, type Lang } from './i18n'
import { ProfileComparisonPanel } from './ProfileComparison'
import type { ProfileComparison, ProfileDraft, StrategyProfile , Denial} from './api'
import { DataNote } from './DataNote'
import { useAtlasWindows } from './shifts'
import { fmtDate } from './time'

const VACIO: ProfileDraft = {
  name: '', division: '', framework: '', rationale: '',
  hotspotMethod: 'grid_count', timeWindow: 'ALL_DAY',
  topHotspots: 60, minIncidents: 10, trainingDays: 180,
  optimizerPreset: 'protect-base', policyOverrides: '{}',
  targets: { onTimeRate: null, presenceRate: null, maxDeferredPerShift: null },
}


/**
 * El perfil de estrategia operacional (§2.2, Figura 5).
 *
 * Es cómo patrulla esta agencia: enfoque, método analítico, táctica y las metas contra las que se
 * va a medir. Antes de esto, cada turno se planificaba con las perillas que alguien recordó poner
 * ese día; el escenario guardaba su política, sí, pero nadie había aprobado esa política.
 *
 * Los tres botones son de tres personas distintas y la pantalla solo muestra el que a quien mira
 * le toca. No es cosmética: es lo que impide que la decisión de la agencia se vuelva el trámite de
 * una sola persona.
 */
export function StrategyScreen({
  lang, profiles, busy, canPropose, canValidate, canApprove, onPropose, onUpdate, onAct,
  comparison, onCompare, loaded, problem,
}: {
  lang: Lang
  profiles: StrategyProfile[]
  busy: boolean
  comparison: ProfileComparison | null
  onCompare: (a: string, b: string) => void
  canPropose: boolean
  canValidate: boolean
  canApprove: boolean
  onPropose: (draft: ProfileDraft) => void
  onUpdate: (profileId: string, draft: ProfileDraft) => void
  onAct: (profileId: string, action: 'validate' | 'return' | 'approve' | 'retire', note: string) => void
  loaded: boolean
  problem: Denial | null
}) {
  const ventanas = useAtlasWindows()
  const [draft, setDraft] = useState<ProfileDraft>(VACIO)
  // Dos perfiles marcados: comparar es la pregunta con la que se decide la versión siguiente.
  const [comparados, setComparados] = useState<string[]>([])
  const [editando, setEditando] = useState<string | null>(null)
  const [nota, setNota] = useState<Record<string, string>>({})

  // Rige uno por alcance: el de la agencia y, encima, el de cada división que tenga el suyo.
  // Mostrar solo el primero hacía creer que el de la agencia había dejado de regir al aprobar
  // uno de división (ES-03).
  const activos = profiles
    .filter((p) => p.status === 'Active')
    .sort((a, b) => (a.division === '' ? -1 : b.division === '' ? 1 : a.division.localeCompare(b.division)))

  // Editar un borrador carga sus valores; salir de él vuelve a dejar el formulario limpio.
  useEffect(() => {
    if (editando === null) return
    const p = profiles.find((x) => x.profileId === editando)
    if (p) setDraft({ ...p })
  }, [editando, profiles])

  const cuando = (iso: string | null) =>
    iso ? fmtDate(iso, lang) : '—'

  const numero = (clave: 'topHotspots' | 'minIncidents' | 'trainingDays', min: number, max: number) => (
    <input
      type="number" min={min} max={max} value={draft[clave]} disabled={busy} style={{ width: 90 }}
      onChange={(e) => setDraft({ ...draft, [clave]: Number(e.target.value) })}
      aria-label={translate(lang, `strategy.${clave}`)}
    />
  )

  const meta = (clave: 'onTimeRate' | 'presenceRate') => (
    <input
      type="number" min={0} max={1} step={0.05} style={{ width: 90 }} disabled={busy}
      value={draft.targets[clave] ?? ''}
      onChange={(e) => setDraft({
        ...draft,
        targets: { ...draft.targets, [clave]: e.target.value === '' ? null : Number(e.target.value) },
      })}
      aria-label={translate(lang, `strategy.${clave}`)}
    />
  )

  return (
    <div className="layout single">
      <aside className="side full">
        <p className="notice">{translate(lang, 'strategy.help')}</p>

        <section className="panel">
          <h2>{translate(lang, 'strategy.active')}</h2>
          {activos.length > 0 ? activos.map((activo) => (
            <div className="card" key={activo.profileId}>
              <div className="row-line">
                <strong>{activo.name} v{activo.version}</strong>
                <span className="chip ok">{translate(lang, 'strategyStatus.Active')}</span>
              </div>
              <div className="meta">
                {activo.division === ''
                  ? translate(lang, 'strategy.scopeAgency')
                  : translate(lang, 'strategy.scopeDivision', [activo.division])}
              </div>
              <div className="meta">{activo.framework}</div>
              <p className="notice">{activo.rationale}</p>
              {/* El método analítico es de la agencia: uno de división no lo fija, y mostrar los
                  valores por omisión hacía creer que la división calcula distinto. */}
              {activo.division === '' && (
                <div className="meta">
                  {translate(lang, 'strategy.method', [
                    activo.hotspotMethod, activo.timeWindow, activo.topHotspots, activo.trainingDays,
                  ])}
                </div>
              )}
              <div className="meta">
                {translate(lang, 'strategy.tactic', [activo.optimizerPreset, activo.policyOverrides])}
              </div>
              <div className="meta">
                {translate(lang, 'strategy.goals', [
                  activo.targets.onTimeRate === null ? '—' : `${Math.round(activo.targets.onTimeRate * 100)}%`,
                  activo.targets.presenceRate === null ? '—' : `${Math.round(activo.targets.presenceRate * 100)}%`,
                  activo.targets.maxDeferredPerShift ?? '—',
                ])}
              </div>
              <div className="meta">
                {translate(lang, 'strategy.signed', [
                  activo.proposedBy, activo.validatedBy || '—', activo.approvedBy, cuando(activo.approvedAt),
                ])}
              </div>
            </div>
          )) : (
            <div className="card"><p className="notice">{translate(lang, 'strategy.none')}</p></div>
          )}
        </section>

        {canPropose && (
          <section className="panel">
            <h2>{translate(lang, editando ? 'strategy.editDraft' : 'strategy.propose')}</h2>
            <p className="notice">{translate(lang, 'strategy.proposeHelp')}</p>

            <div className="card">
              <label className="rule">
                {translate(lang, 'strategy.name')}
                <input value={draft.name} disabled={busy || editando !== null}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </label>
              {/* El alcance va antes que nada porque decide qué se puede llenar después: un
                  perfil de división no lleva método analítico. Ver strategy.divisionHelp. */}
              <label className="rule">
                {translate(lang, 'strategy.division')}
                <input
                  value={draft.division} disabled={busy || editando !== null}
                  placeholder={translate(lang, 'strategy.divisionAll')}
                  onChange={(e) => setDraft({ ...draft, division: e.target.value.trim() })} />
              </label>
              {draft.division !== '' && (
                <p className="notice warning">{translate(lang, 'strategy.divisionHelp')}</p>
              )}
              <label className="rule">
                {translate(lang, 'strategy.framework')}
                <input value={draft.framework} disabled={busy}
                  onChange={(e) => setDraft({ ...draft, framework: e.target.value })} />
              </label>
              <label className="rule">
                {translate(lang, 'strategy.rationale')}
                <textarea rows={3} value={draft.rationale} disabled={busy}
                  placeholder={translate(lang, 'strategy.rationaleHelp')}
                  onChange={(e) => setDraft({ ...draft, rationale: e.target.value })} />
              </label>
            </div>

            <div className="grid">
              {draft.division === '' && (
              <div className="card">
                <h3>{translate(lang, 'strategy.analytics')}</h3>
                <p className="notice">{translate(lang, 'strategy.analyticsHelp')}</p>
                <label className="rule">
                  {translate(lang, 'strategy.timeWindow')}
                  <select value={draft.timeWindow} disabled={busy}
                    onChange={(e) => setDraft({ ...draft, timeWindow: e.target.value })}>
                    {ventanas.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
                <label className="rule">{translate(lang, 'strategy.topHotspots')}{numero('topHotspots', 1, 500)}</label>
                <label className="rule">{translate(lang, 'strategy.minIncidents')}{numero('minIncidents', 1, 100)}</label>
                <label className="rule">{translate(lang, 'strategy.trainingDays')}{numero('trainingDays', 30, 1825)}</label>
              </div>
              )}

              <div className="card">
                <h3>{translate(lang, 'strategy.tacticTitle')}</h3>
                <p className="notice">{translate(lang, 'strategy.tacticHelp')}</p>
                <label className="rule">
                  {translate(lang, 'strategy.preset')}
                  <select value={draft.optimizerPreset} disabled={busy}
                    onChange={(e) => setDraft({ ...draft, optimizerPreset: e.target.value })}>
                    <option value="protect-base">protect-base</option>
                    <option value="base">base</option>
                  </select>
                </label>
                <label className="rule">
                  {translate(lang, 'strategy.overrides')}
                  <input value={draft.policyOverrides} disabled={busy}
                    onChange={(e) => setDraft({ ...draft, policyOverrides: e.target.value })} />
                </label>
              </div>

              <div className="card">
                <h3>{translate(lang, 'strategy.targets')}</h3>
                <p className="notice">{translate(lang, 'strategy.targetsHelp')}</p>
                <label className="rule">{translate(lang, 'strategy.onTimeRate')}{meta('onTimeRate')}</label>
                <label className="rule">{translate(lang, 'strategy.presenceRate')}{meta('presenceRate')}</label>
                <label className="rule">
                  {translate(lang, 'strategy.maxDeferred')}
                  <input type="number" min={0} style={{ width: 90 }} disabled={busy}
                    value={draft.targets.maxDeferredPerShift ?? ''}
                    onChange={(e) => setDraft({
                      ...draft,
                      targets: {
                        ...draft.targets,
                        maxDeferredPerShift: e.target.value === '' ? null : Number(e.target.value),
                      },
                    })} />
                </label>
              </div>
            </div>

            <div className="actions">
              <button className="primary" disabled={busy}
                onClick={() => {
                  if (editando) onUpdate(editando, draft)
                  else onPropose(draft)
                }}>
                {translate(lang, editando ? 'strategy.save' : 'strategy.proposeAction')}
              </button>
              {editando && (
                <button disabled={busy} onClick={() => { setEditando(null); setDraft(VACIO) }}>
                  {translate(lang, 'strategy.cancel')}
                </button>
              )}
            </div>
          </section>
        )}

        {comparison && (
          <ProfileComparisonPanel
            lang={lang} comparison={comparison}
            onClose={() => { setComparados([]); onCompare('', '') }}
          />
        )}

        <section className="panel">
          <h2>{translate(lang, 'strategy.all', [profiles.length])}</h2>
          <DataNote lang={lang} loaded={loaded} problem={problem} count={profiles.length} empty="strategy.noneYet" />
          {profiles.length > 1 && <p className="notice">{translate(lang, 'strategy.compareHint')}</p>}

          {profiles.map((p) => (
            <div key={p.profileId} className="card">
              <div className="row-line">
                <strong>{p.name} v{p.version}</strong>
                <span className={p.status === 'Active' ? 'chip ok' : 'chip'}>
                  {/* Devuelto es borrador con nota: le toca al analista, no al planificador. */}
                  {translate(lang, p.status === 'Draft' && p.note ? 'strategyStatus.Returned' : `strategyStatus.${p.status}`)}
                </span>
                {p.division !== '' && <span className="chip">{p.division}</span>}
                <div className="spacer" />
                <span className="meta">{p.framework}</span>
              </div>

              {/* Comparar dos perfiles es lo que cierra el lazo de la Figura 5: se ve cómo le fue
                  a cada uno, y con eso se decide qué pedirle al siguiente. */}
              <label className="meta">
                <input
                  type="checkbox" disabled={busy}
                  checked={comparados.includes(p.profileId)}
                  onChange={(e) => {
                    const elegidos = e.target.checked
                      ? [...comparados, p.profileId].slice(-2)
                      : comparados.filter((x) => x !== p.profileId)
                    setComparados(elegidos)
                    if (elegidos.length === 2) onCompare(elegidos[0]!, elegidos[1]!)
                  }}
                />
                {' '}{translate(lang, 'compareProfiles.open')}
              </label>

              {/* La nota es de la última decisión: devolverlo, aprobarlo o retirarlo. Todas
                  decían «Devuelto», también la de una aprobación (ES-03). */}
              {p.note && <p className="notice">{translate(lang,
                p.status === 'Active' ? 'strategy.approvedNote'
                  : p.status === 'Retired' ? 'strategy.retiredNote'
                  : p.status === 'Validated' ? 'strategy.validatedNote' : 'strategy.returned', [p.note])}</p>}

              <div className="meta">
                {translate(lang, 'strategy.proposedBy', [p.proposedBy, cuando(p.proposedAt)])}
              </div>

              {/* Cada acto se le ofrece solo a quien le toca hacerlo, y sin ninguno no hay nota
                  que escribir: al jefe le aparecía la caja sola sobre una propuesta sin validar. */}
              {((p.status === 'Draft' && (canPropose || canValidate))
                || (p.status === 'Validated' && (canValidate || canApprove))) && (
                <div className="actions">
                  <input
                    value={nota[p.profileId] ?? ''} disabled={busy} style={{ minWidth: 220 }}
                    placeholder={translate(lang, 'strategy.note')}
                    onChange={(e) => setNota({ ...nota, [p.profileId]: e.target.value })}
                    aria-label={translate(lang, 'strategy.note')}
                  />
                  {canPropose && p.status === 'Draft' && (
                    <button disabled={busy} onClick={() => setEditando(p.profileId)}>
                      {translate(lang, 'strategy.edit')}
                    </button>
                  )}
                  {canValidate && p.status === 'Draft' && (
                    <button disabled={busy}
                      onClick={() => onAct(p.profileId, 'validate', nota[p.profileId] ?? '')}>
                      {translate(lang, 'strategy.validate')}
                    </button>
                  )}
                  {/* Devolver es de quien valida y de quien aprueba: el jefe también dice «así no» (H-92). */}
                  {(canValidate || canApprove) && p.status === 'Validated' && (
                    <button disabled={busy}
                      onClick={() => onAct(p.profileId, 'return', nota[p.profileId] ?? '')}>
                      {translate(lang, 'strategy.return')}
                    </button>
                  )}
                  {canApprove && p.status === 'Validated' && (
                    <button className="primary" disabled={busy}
                      onClick={() => onAct(p.profileId, 'approve', nota[p.profileId] ?? '')}>
                      {translate(lang, 'strategy.approve')}
                    </button>
                  )}
                </div>
              )}

              {canApprove && p.status === 'Active' && (
                <div className="actions">
                  <button disabled={busy} onClick={() => onAct(p.profileId, 'retire', '')}>
                    {translate(lang, 'strategy.retire')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </section>
      </aside>
    </div>
  )
}
